/**
 * @engine/physics2d - Built-in Physics Engine Adapter
 *
 * Wraps the custom PhysicsWorld behind the IPhysicsEngine2D interface
 * so it can be swapped with Rapier2D or other backends.
 */

import {
  type IPhysicsEngine2D,
  type PhysicsWorldOptions,
  type RigidBodyDescriptor,
  type ColliderDescriptor,
  type BodyTransform,
  type PhysicsBodyHandle,
  type PhysicsColliderHandle,
  type PhysicsBodyEventHandlers,
  type PhysVec2,
  type RaycastHit,
  type GravityZoneDescriptor,
  type BuoyancyZoneDescriptor,
  type WindZoneDescriptor,
  type SurfaceEffectorDescriptor,
  PhysicsBodyType,
} from './engine-interface';
import { PhysicsWorld } from './dynamics/PhysicsWorld';
import {
  BodyType,
  ColliderType,
  CapsuleDirection,
  SleepState,
  type ColliderState,
  type PhysicsWorldConfig,
  type GravityZoneConfig,
  computePolygonNormals,
} from './types';

// ============================================================================
// BODY TYPE MAPPING
// ============================================================================

function toEngineBodyType(bt: PhysicsBodyType): BodyType {
  switch (bt) {
    case PhysicsBodyType.Dynamic: return BodyType.Dynamic;
    case PhysicsBodyType.Kinematic: return BodyType.Kinematic;
    case PhysicsBodyType.Static: return BodyType.Static;
  }
}

function fromEngineBodyType(bt: BodyType): PhysicsBodyType {
  switch (bt) {
    case BodyType.Dynamic: return PhysicsBodyType.Dynamic;
    case BodyType.Kinematic: return PhysicsBodyType.Kinematic;
    case BodyType.Static: return PhysicsBodyType.Static;
  }
}

// ============================================================================
// ADAPTER
// ============================================================================

export class BuiltinPhysicsEngine implements IPhysicsEngine2D {
  readonly name = 'Built-in';

  private _world: PhysicsWorld | null = null;

  // ──────── Lifecycle ────────

  async init(): Promise<void> {
    // No async setup needed for the built-in solver
  }

  createWorld(config: PhysicsWorldOptions): void {
    const cfg: Partial<PhysicsWorldConfig> = {};
    if (config.gravity) cfg.gravity = { ...config.gravity };
    if (config.fixedTimestep !== undefined) cfg.fixedTimestep = config.fixedTimestep;
    if (config.velocityIterations !== undefined) cfg.velocityIterations = config.velocityIterations;
    if (config.positionIterations !== undefined) cfg.positionIterations = config.positionIterations;
    if (config.allowSleeping !== undefined) cfg.allowSleeping = config.allowSleeping;
    this._world = new PhysicsWorld(cfg);
  }

  destroyWorld(): void {
    if (this._world) {
      this._world.clear();
      this._world = null;
    }
  }

  dispose(): void {
    this.destroyWorld();
  }

  // ──────── Bodies ────────

  createBody(desc: RigidBodyDescriptor): PhysicsBodyHandle {
    this._assertWorld();
    const bodyIndex = this._world!.createBody(
      desc.guid,
      desc.gameObjectId,
      toEngineBodyType(desc.bodyType),
      desc.position,
      desc.rotation,
    );

    // Apply optional properties
    const body = this._world!.getBody(bodyIndex as number);
    if (body) {
      if (desc.mass !== undefined && desc.mass > 0) {
        body.mass = desc.mass;
        body.invMass = 1 / desc.mass;
      }
      if (desc.linearDamping !== undefined) body.linearDamping = desc.linearDamping;
      if (desc.angularDamping !== undefined) body.angularDamping = desc.angularDamping;
      if (desc.gravityScale !== undefined) body.gravityScale = desc.gravityScale;
      if (desc.constraints !== undefined) body.constraints = desc.constraints;
    }

    return bodyIndex;
  }

  removeBody(handle: PhysicsBodyHandle): void {
    this._assertWorld();
    this._world!.removeBody(handle as number);
  }

  // ──────── Colliders ────────

  addCollider(bodyHandle: PhysicsBodyHandle, desc: ColliderDescriptor): PhysicsColliderHandle {
    this._assertWorld();

    const partial: Partial<ColliderState> = {
      offset: desc.offset ?? { x: 0, y: 0 },
      rotation: desc.rotation ?? 0,
      isTrigger: desc.isTrigger ?? false,
      material: desc.material
        ? {
            friction: desc.material.friction,
            bounciness: desc.material.bounciness,
            density: desc.material.density,
            // CombineMode enums have identical numeric values across interfaces
            frictionCombine: desc.material.frictionCombine as number | undefined,
            bouncinessCombine: desc.material.bouncinessCombine as number | undefined,
          }
        : undefined,
      layerMask: desc.layerMask,
      collisionMask: desc.collisionMask,
      oneWay: desc.oneWay ?? false,
      oneWayDirection: desc.oneWayDirection ? { ...desc.oneWayDirection } : { x: 0, y: 1 },
    };

    const shape = desc.shape;
    switch (shape.type) {
      case 'box':
        partial.type = ColliderType.Box;
        partial.halfExtents = { ...shape.halfExtents };
        break;
      case 'circle':
        partial.type = ColliderType.Circle;
        partial.radius = shape.radius;
        break;
      case 'capsule':
        partial.type = ColliderType.Capsule;
        partial.radius = shape.radius;
        partial.capsuleHeight = shape.halfHeight * 2 + shape.radius * 2;
        partial.capsuleDirection = CapsuleDirection.Vertical;
        break;
      case 'polygon': {
        partial.type = ColliderType.Polygon;
        const verts = shape.vertices.map(v => ({ ...v }));
        partial.vertices = verts;
        partial.normals = computePolygonNormals(verts);
        partial.vertexCount = verts.length;
        break;
      }
    }

    return this._world!.addCollider(bodyHandle as number, partial);
  }

  removeCollider(handle: PhysicsColliderHandle): void {
    this._assertWorld();
    this._world!.removeCollider(handle as number);
  }

  // ──────── Events ────────

  setEventHandler(handle: PhysicsBodyHandle, handlers: PhysicsBodyEventHandlers): void {
    this._assertWorld();
    this._world!.setEventHandlers(handle as number, {
      onCollisionEnter: handlers.onCollisionEnter
        ? (e) => {
          const c = e.manifold.contacts[0];
          handlers.onCollisionEnter!(e.otherGuid, c?.point.x ?? 0, c?.point.y ?? 0, e.manifold.normal.x, e.manifold.normal.y);
        }
        : undefined,
      onCollisionStay: handlers.onCollisionStay
        ? (e) => {
          const c = e.manifold.contacts[0];
          handlers.onCollisionStay!(e.otherGuid, c?.point.x ?? 0, c?.point.y ?? 0, e.manifold.normal.x, e.manifold.normal.y);
        }
        : undefined,
      onCollisionExit: handlers.onCollisionExit
        ? (e) => handlers.onCollisionExit!(e.otherGuid, 0, 0, 0, 0)
        : undefined,
      onTriggerEnter: handlers.onTriggerEnter
        ? (e) => handlers.onTriggerEnter!(e.otherGuid)
        : undefined,
      onTriggerStay: handlers.onTriggerStay
        ? (e) => handlers.onTriggerStay!(e.otherGuid)
        : undefined,
      onTriggerExit: handlers.onTriggerExit
        ? (e) => handlers.onTriggerExit!(e.otherGuid)
        : undefined,
    });
  }

  // ──────── Simulation ────────

  step(dt: number): void {
    this._assertWorld();
    this._world!.update(dt);
  }

  // ──────── Read-back ────────

  forEachBody(fn: (body: BodyTransform) => void): void {
    if (!this._world) return;
    this._world.forEachBody((b) => {
      fn({
        guid: b.guid,
        gameObjectId: b.gameObjectId,
        bodyType: fromEngineBodyType(b.bodyType),
        position: { x: b.position.x, y: b.position.y },
        rotation: b.rotation,
      });
    });
  }

  // ──────── Forces ────────

  applyForce(handle: PhysicsBodyHandle, force: PhysVec2): void {
    this._assertWorld();
    this._world!.applyForce(handle as number, force);
  }

  applyImpulse(handle: PhysicsBodyHandle, impulse: PhysVec2): void {
    this._assertWorld();
    this._world!.applyImpulse(handle as number, impulse);
  }

  applyTorque(handle: PhysicsBodyHandle, torque: number): void {
    this._assertWorld();
    this._world!.applyTorque(handle as number, torque);
  }

  applyAngularImpulse(handle: PhysicsBodyHandle, impulse: number): void {
    this._assertWorld();
    this._world!.applyAngularImpulse(handle as number, impulse);
  }

  // ──────── Velocity ────────

  getLinearVelocity(handle: PhysicsBodyHandle): PhysVec2 {
    const body = this._world?.getBody(handle as number);
    return body ? { x: body.linearVelocity.x, y: body.linearVelocity.y } : { x: 0, y: 0 };
  }

  setLinearVelocity(handle: PhysicsBodyHandle, velocity: PhysVec2): void {
    const body = this._world?.getBody(handle as number);
    if (body) {
      body.linearVelocity.x = velocity.x;
      body.linearVelocity.y = velocity.y;
    }
  }

  getAngularVelocity(handle: PhysicsBodyHandle): number {
    return this._world?.getBody(handle as number)?.angularVelocity ?? 0;
  }

  setAngularVelocity(handle: PhysicsBodyHandle, velocity: number): void {
    const body = this._world?.getBody(handle as number);
    if (body) body.angularVelocity = velocity;
  }

  // ──────── Body Properties ────────

  setLinearDamping(handle: PhysicsBodyHandle, damping: number): void {
    const body = this._world?.getBody(handle as number);
    if (body) body.linearDamping = damping;
  }

  setAngularDamping(handle: PhysicsBodyHandle, damping: number): void {
    const body = this._world?.getBody(handle as number);
    if (body) body.angularDamping = damping;
  }

  setGravityScale(handle: PhysicsBodyHandle, scale: number): void {
    const body = this._world?.getBody(handle as number);
    if (body) body.gravityScale = scale;
  }

  getMass(handle: PhysicsBodyHandle): number {
    return this._world?.getBody(handle as number)?.mass ?? 0;
  }

  setMass(handle: PhysicsBodyHandle, mass: number): void {
    const body = this._world?.getBody(handle as number);
    if (body) {
      body.mass = mass;
      body.invMass = mass > 0 ? 1 / mass : 0;
    }
  }

  // ──────── Sleep ────────

  sleep(handle: PhysicsBodyHandle): void {
    this._world?.sleepBody(handle as number);
  }

  wake(handle: PhysicsBodyHandle): void {
    this._world?.wakeBody(handle as number);
  }

  isSleeping(handle: PhysicsBodyHandle): boolean {
    const body = this._world?.getBody(handle as number);
    return body?.sleepState === SleepState.Sleeping;
  }

  // ──────── Overlap Queries ────────

  overlapCircle(center: PhysVec2, radius: number, layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    return this._world.overlapCircle(center, radius, layerMask);
  }

  overlapBox(center: PhysVec2, halfExtents: PhysVec2, layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    return this._world.overlapBox(center, halfExtents, layerMask);
  }

  overlapPoint(point: PhysVec2, layerMask?: number): PhysicsBodyHandle[] {
    if (!this._world) return [];
    return this._world.overlapPoint(point, layerMask);
  }

  // ──────── Queries ────────

  raycast(origin: PhysVec2, direction: PhysVec2, maxDistance: number, _layerMask?: number): RaycastHit | null {
    if (!this._world) return null;
    const hit = this._world.raycast(origin, direction, maxDistance, _layerMask);
    if (!hit) return null;
    return {
      point: hit.point,
      normal: hit.normal,
      distance: hit.distance,
      bodyHandle: hit.bodyIndex,
    };
  }

  raycastAll(origin: PhysVec2, direction: PhysVec2, maxDistance: number, _layerMask?: number): RaycastHit[] {
    if (!this._world) return [];
    return this._world.raycastAll(origin, direction, maxDistance, _layerMask).map(hit => ({
      point: hit.point,
      normal: hit.normal,
      distance: hit.distance,
      bodyHandle: hit.bodyIndex,
    }));
  }

  // ──────── Gravity Zones ────────

  addGravityZone(desc: GravityZoneDescriptor): void {
    this._assertWorld();
    // Cast enum values (identical numeric values across interfaces)
    const config: GravityZoneConfig = {
      type: (desc.type as number) ?? 0,
      shape: (desc.shape as number) ?? 0,
      gravity: desc.gravity ? { ...desc.gravity } : { x: 0, y: 0 },
      strength: desc.strength ?? 9.81,
      falloff: (desc.falloff as number) ?? 1,
      innerRadius: desc.innerRadius ?? 0,
      outerRadius: desc.outerRadius ?? 5,
      size: desc.size ? { ...desc.size } : { x: 4, y: 2 },
      direction: desc.direction ?? 0,
      points: desc.points ? desc.points.map(p => ({ ...p })) : [],
      priority: desc.priority ?? 0,
      affectedLayers: desc.affectedLayers ?? 0xFFFF,
    };
    const position = desc.position ?? { x: 0, y: 0 };
    const rotation = desc.rotation ?? 0;
    const scale = desc.scale ?? { x: 1, y: 1 };
    this._world!.addGravityZone(desc.guid, position, rotation, scale, config);
  }

  updateGravityZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    if (!this._world) return;
    this._world.updateGravityZonePosition(guid, position, rotation, scale);
  }

  updateGravityZone(guid: string, desc: Partial<GravityZoneDescriptor>): void {
    if (!this._world) return;
    const config: Partial<GravityZoneConfig> = {};
    if (desc.type !== undefined) config.type = desc.type as number;
    if (desc.shape !== undefined) config.shape = desc.shape as number;
    if (desc.gravity) config.gravity = { ...desc.gravity };
    if (desc.strength !== undefined) config.strength = desc.strength;
    if (desc.falloff !== undefined) config.falloff = desc.falloff as number;
    if (desc.innerRadius !== undefined) config.innerRadius = desc.innerRadius;
    if (desc.outerRadius !== undefined) config.outerRadius = desc.outerRadius;
    if (desc.size) config.size = { ...desc.size };
    if (desc.direction !== undefined) config.direction = desc.direction;
    if (desc.points) config.points = desc.points.map(p => ({ ...p }));
    if (desc.priority !== undefined) config.priority = desc.priority;
    if (desc.affectedLayers !== undefined) config.affectedLayers = desc.affectedLayers;
    this._world.updateGravityZoneConfig(guid, config);
  }

  removeGravityZone(guid: string): void {
    if (!this._world) return;
    this._world.removeGravityZone(guid);
  }

  // ──────── Buoyancy Zones ────────

  addBuoyancyZone(desc: BuoyancyZoneDescriptor): void {
    this._assertWorld();
    const position = desc.position ?? { x: 0, y: 0 };
    const rotation = desc.rotation ?? 0;
    const scale = desc.scale ?? { x: 1, y: 1 };
    this._world!.addBuoyancyZone(desc.guid, position, rotation, scale, {
      density: desc.density ?? 1,
      linearDrag: desc.linearDrag ?? 3,
      angularDrag: desc.angularDrag ?? 1,
      flow: desc.flow ?? { x: 0, y: 0 },
      surfaceNormal: desc.surfaceNormal ?? { x: 0, y: 1 },
      width: desc.width ?? 10,
      height: desc.height ?? 5,
      priority: desc.priority ?? 0,
      affectedLayers: desc.affectedLayers ?? 0xFFFF,
    });
  }

  updateBuoyancyZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    if (!this._world) return;
    this._world.updateBuoyancyZonePosition(guid, position, rotation, scale);
  }

  updateBuoyancyZone(guid: string, desc: Partial<BuoyancyZoneDescriptor>): void {
    if (!this._world) return;
    this._world.updateBuoyancyZoneConfig(guid, {
      density: desc.density,
      linearDrag: desc.linearDrag,
      angularDrag: desc.angularDrag,
      flow: desc.flow ? { ...desc.flow } : undefined,
      surfaceNormal: desc.surfaceNormal ? { ...desc.surfaceNormal } : undefined,
      width: desc.width,
      height: desc.height,
      priority: desc.priority,
      affectedLayers: desc.affectedLayers,
    });
  }

  removeBuoyancyZone(guid: string): void {
    if (!this._world) return;
    this._world.removeBuoyancyZone(guid);
  }

  // ──────── Wind Zones ────────

  addWindZone(desc: WindZoneDescriptor): void {
    this._assertWorld();
    const position = desc.position ?? { x: 0, y: 0 };
    const rotation = desc.rotation ?? 0;
    const scale = desc.scale ?? { x: 1, y: 1 };
    this._world!.addWindZone(desc.guid, position, rotation, scale, {
      mode: desc.mode ?? 0,
      direction: desc.direction ?? { x: 1, y: 0 },
      strength: desc.strength ?? 10,
      turbulence: desc.turbulence ?? 0,
      turbulenceFrequency: desc.turbulenceFrequency ?? 1,
      pulse: desc.pulse ?? 0,
      pulseFrequency: desc.pulseFrequency ?? 1,
      shape: desc.shape ?? 1,
      radius: desc.radius ?? 5,
      width: desc.width ?? 8,
      height: desc.height ?? 4,
      falloff: desc.falloff ?? 0,
      falloffStart: desc.falloffStart ?? 0,
      priority: desc.priority ?? 0,
      affectedLayers: desc.affectedLayers ?? 0xFFFF,
    });
  }

  updateWindZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    if (!this._world) return;
    this._world.updateWindZonePosition(guid, position, rotation, scale);
  }

  updateWindZone(guid: string, desc: Partial<WindZoneDescriptor>): void {
    if (!this._world) return;
    this._world.updateWindZoneConfig(guid, {
      mode: desc.mode,
      direction: desc.direction ? { ...desc.direction } : undefined,
      strength: desc.strength,
      turbulence: desc.turbulence,
      turbulenceFrequency: desc.turbulenceFrequency,
      pulse: desc.pulse,
      pulseFrequency: desc.pulseFrequency,
      shape: desc.shape,
      radius: desc.radius,
      width: desc.width,
      height: desc.height,
      falloff: desc.falloff,
      falloffStart: desc.falloffStart,
      priority: desc.priority,
      affectedLayers: desc.affectedLayers,
    });
  }

  removeWindZone(guid: string): void {
    if (!this._world) return;
    this._world.removeWindZone(guid);
  }

  // ──────── Surface Effectors ────────

  addSurfaceEffector(desc: SurfaceEffectorDescriptor): void {
    // Surface effectors are handled via contact callbacks, not zones
    // For now, store them in a map for collider lookup
    // TODO: Implement surface effector contact handling
  }

  updateSurfaceEffector(guid: string, desc: Partial<SurfaceEffectorDescriptor>): void {
    // TODO: Implement
  }

  removeSurfaceEffector(guid: string): void {
    // TODO: Implement
  }

  // ──────── Diagnostics ────────

  getDiagnostics(): {
    bodyCount: number;
    colliderCount: number;
    activeBodies: number;
    sleepingBodies: number;
    broadPhasePairs: number;
    narrowPhaseContacts: number;
    islandCount: number;
    stepCount: number;
    stepTimeMs: number;
  } {
    if (!this._world) {
      return {
        bodyCount: 0,
        colliderCount: 0,
        activeBodies: 0,
        sleepingBodies: 0,
        broadPhasePairs: 0,
        narrowPhaseContacts: 0,
        islandCount: 0,
        stepCount: 0,
        stepTimeMs: 0,
      };
    }
    
    const worldDiag = this._world.getDiagnostics();
    return {
      ...worldDiag,
      stepTimeMs: 0, // Not tracked in built-in engine
    };
  }

  // ──────── Helpers ────────

  private _assertWorld(): void {
    if (!this._world) throw new Error('[BuiltinPhysicsEngine] No world created. Call createWorld() first.');
  }
}
